import { Classes, MaybeElement, TabId, TabProps } from "@blueprintjs/core";
import classNames from "classnames";
import React, { useCallback } from "react";

/*
A re-implementation of Blueprint's internal `<TabTitle>` component. The
implementation should almost exactly match the original. The difference is that
the logic has been extracted to a hook so that it can be shared with other
customizations of the Tab component.

See [source code](https://github.com/palantir/blueprint/blob/61f488f59ce56039d396bd2e8d8df46bc206327e/packages/core/src/components/tabs/tabTitle.tsx).
*/

interface InternalHexTabProps extends Omit<TabProps, "icon"> {
  icon?: MaybeElement;
  children?: React.ReactNode;
  onClick: (id: TabId, event: React.MouseEvent<HTMLElement>) => void;
  parentId: TabId;
  selected: boolean;
}

/**
 * @ignore
 * @private
 */
export const InternalHexTab = React.memo(function InternalHexTab(
  props: InternalHexTabProps,
) {
  const finalProps = useInternalHexTab<HTMLDivElement>(props);

  return <div {...finalProps} />;
});

/**
 * @ignore
 * @private
 */
export const useInternalHexTab = <TElement extends HTMLElement>(
  props: Pick<
    InternalHexTabProps,
    | "children"
    | "className"
    | "disabled"
    | "icon"
    | "id"
    | "onClick"
    | "parentId"
    | "selected"
    | "title"
  > &
    Omit<ElementAttributes<TElement>, "id" | "title" | "onClick">,
): ElementAttributes<TElement> => {
  const {
    children: childrenProp,
    className,
    disabled = false,
    icon,
    id,
    onClick,
    parentId,
    selected = false,
    title,
    ...htmlProps
  } = props;

  const { tabPanelId, tabTitleId } = generateTabIds(parentId, id);
  const htmlTabIndex = selected ? 0 : -1;

  const handleClick = useCallback<React.MouseEventHandler<HTMLDivElement>>(
    (event) => {
      onClick(id, event);
    },
    [onClick, id],
  );

  const children = (
    <>
      {icon}
      {title}
      {childrenProp}
    </>
  );

  const finalProps = {
    "aria-controls": tabPanelId,
    "aria-disabled": disabled,
    "aria-expanded": selected,
    "aria-selected": selected,
    className: classNames(Classes.TAB, className),
    "data-tab-id": id,
    id: tabTitleId,
    role: "tab",
    tabIndex: disabled ? undefined : htmlTabIndex,
    onClick: disabled ? undefined : handleClick,
    children,
    ...htmlProps,
  } as ElementAttributes<TElement>;

  return finalProps;
};

function generateTabIds(
  parentId: TabId,
  tabId: TabId,
): { tabPanelId: string; tabTitleId: string } {
  return {
    tabPanelId: `${Classes.TAB_PANEL}_${parentId}_${tabId}`,
    tabTitleId: `${Classes.TAB}-title_${parentId}_${tabId}`,
  };
}

/**
 * Helper type to allow spreading html props of structurally different
 * elements types
 */
type ElementAttributes<TElement extends HTMLElement> =
  TElement extends HTMLAnchorElement
    ? React.AnchorHTMLAttributes<HTMLAnchorElement>
    : React.HTMLAttributes<HTMLDivElement>;
